import { cn } from "../../lib/cn";
import type { ButtonHTMLAttributes, ReactNode } from "react";

interface GradientButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "violet" | "cyan";
  /** Если true — кнопка займёт всю ширину контейнера. */
  block?: boolean;
}

/**
 * Премиум-CTA с градиентным фоном и shimmer-блеском при hover.
 * Используется на Auth, Onboarding, Welcome, FinalCTA — там, где «герой-кнопка».
 */
export function GradientButton({
  children,
  size = "md",
  variant = "primary",
  block,
  className,
  ...rest
}: GradientButtonProps) {
  const sizeMap = {
    sm: "h-9 px-4 text-small",
    md: "h-11 px-5 text-[14px]",
    lg: "h-12 sm:h-14 px-6 sm:px-7 text-[15px]",
  };
  const variantBg = {
    primary: "var(--grad-cta)",
    violet: "linear-gradient(135deg, rgb(var(--violet)) 0%, rgb(var(--pink)) 100%)",
    cyan: "linear-gradient(135deg, rgb(var(--cyan)) 0%, rgb(var(--accent-500)) 100%)",
  } as const;
  const shadow = {
    primary: "shadow-accent hover:shadow-accent-lg",
    violet: "shadow-violet",
    cyan: "shadow-cyan",
  };

  return (
    <button
      {...rest}
      className={cn(
        "relative inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-white transition-all overflow-hidden group",
        "shadow-[inset_0_1px_0_rgb(255_255_255/0.2)]",
        "hover:-translate-y-0.5 active:translate-y-0",
        "disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed",
        sizeMap[size],
        shadow[variant],
        block && "w-full",
        className,
      )}
      style={{ backgroundImage: variantBg[variant] }}
    >
      {/* Shimmer при hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 0.25) 50%, transparent 100%)",
        }}
      />
      <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
    </button>
  );
}
